import { useEffect, useState } from 'react';
import { useTablePreferences } from '@/application/container';
import { useSession } from '@/application/usecases/useSession';
import {
  useTableLayout,
  useTableLayouts,
  type TableLayoutControl,
} from '@/application/usecases/useTableLayout';

export interface Arrangement extends TableLayoutControl {
  /**
   * Whether the screen may be drawn yet. True from the first paint for anybody
   * this device has not seen arrange the screen; for somebody it has, true once
   * their account has said how.
   */
  settled: boolean;
}

/**
 * How a person arranged one screen of widgets, and whether to wait for it. The
 * lab's request of 12 October 2026: the Overview "stays this by default".
 *
 * That holds for how fast it comes, too. The arrangement is kept on the
 * account, so it is known only once the account has answered. Waiting for that
 * answer before drawing anything would put every manager's production line one
 * request later than it was, for the sake of the few who moved something. So a
 * screen is drawn at once, as it comes, and what the account says is applied
 * when it arrives: for nearly everybody that is nothing, and nothing moves.
 *
 * The one person that would be wrong for is somebody who did arrange the
 * screen: they would watch the section they took off appear and go, and the
 * server would be asked for it. This device remembers that much about them, a
 * yes under their own id and the screen's key, and for them the screen waits
 * the one request. On a device that has never seen their arrangement the
 * screen comes as it comes for a moment and is then theirs, once.
 *
 * Nothing personal is kept: a yes, which is taken back when they reset the
 * screen. Where the device keeps nothing, private browsing among it, nobody
 * waits and the worst is that moment.
 */
export function useArrangement(layoutKey: string): Arrangement {
  const control = useTableLayout(layoutKey);
  const { isPending, isSuccess } = useTableLayouts();
  const preferences = useTablePreferences();
  const userId = useSession().data?.user.id ?? '';
  const hint = `arranged.${userId}.${layoutKey}`;

  // What this device knew when the screen opened. Read once: it decides only the first paint.
  const [arrangedBefore] = useState(() => preferences.setting(hint) === true);

  const arranged = control.layout !== null;
  useEffect(() => {
    // Only from what the account really said, and only written when it differs from what is kept.
    if (!isSuccess || userId === '') return;
    if ((preferences.setting(hint) === true) !== arranged) preferences.rememberSetting(hint, arranged);
  }, [isSuccess, arranged, hint, userId, preferences]);

  return { ...control, settled: !isPending || !arrangedBefore };
}
